/**
 * The sign-in screen — the first thing anyone sees, and the only screen that
 * renders outside the application shell.
 *
 * Authentication is OAuth against the two existing customer panels. The browser
 * still leaves the SPA for the identity provider, but the first hop is POST
 * /auth/login with a Turnstile token in the body — not a GET with the token in
 * the query string, which would land in access logs. When Turnstile is unset
 * (a laptop, the test suite) the widget is skipped and the POST still starts
 * the flow.
 *
 * Provider buttons stay clickable while the widget is pending. Disabling them
 * until a token exists is the screen that reads as "login is disabled": grey
 * rows, no explanation, and the click handler that would say "complete the
 * check" never runs. start() still refuses a missing token.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import type { AuthProviderOption, ProvidersResponse } from '../api/types';
import { useAuth, type LoginRedirectState } from '../auth/AuthContext';
import TurnstileWidget from '../components/Turnstile';
import { FullPageLoader, IndexNumeral, Logo, PaperButton, Skeleton } from '../components/ui';
import { providersRetryDelayMs } from '../lib/retryBackoff';

/**
 * The callback only ever puts a stable error *code* in the URL, never a message,
 * because a message reflected into a query string is where XSS goes looking. So
 * the wording lives here.
 */
const ERROR_COPY: Record<string, string> = {
  bad_request: 'That sign-in link was no longer valid. It may have been opened twice, or left sitting too long. Please try again.',
  unauthorized: 'Your sign-in could not be completed. Please try again.',
  upstream_error: 'We could not reach that sign-in provider just now. Please try again in a moment.',
  rate_limited: 'Too many sign-in attempts. Please wait a minute and try again.',
  internal_error: 'Something went wrong on our side while signing you in. Please try again.',
  captcha: 'The security check did not succeed. Please complete it and try again.',
};

/**
 * Mirrors the backend's `safeRedirectPath`. This value reaches us through
 * history state or a query string, and it ends up in a redirect target, so it is
 * narrowed to an app-relative path here as well as there.
 */
function safeRedirect(value: string | null | undefined): string {
  if (!value || !value.startsWith('/')) return '/';
  if (value.startsWith('//') || value.includes('\\')) return '/';
  if (value.length > 200) return '/';
  // C0, DEL and C1 — same refusal as backend safeRedirectPath.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f-\u009f]/.test(value)) return '/';
  return value;
}

function providerHint(id: AuthProviderOption['id'], label: string): string {
  if (id === 'vgncloud') return 'Continue with your vgncloud.com account';
  if (id === 'valuegamenode') return 'Continue with your valuegamenode.com account';
  return `Continue with your existing ${label} account`;
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

function ProviderButton({
  provider,
  index,
  disabled,
  pending,
  onPick,
}: {
  provider: AuthProviderOption;
  index: number;
  disabled: boolean;
  pending: boolean;
  onPick: (provider: AuthProviderOption) => void;
}) {
  const inert = disabled || pending;
  return (
    <button
      type="button"
      onClick={() => onPick(provider)}
      disabled={inert}
      className="group flex w-full items-center gap-5 border-t-2 border-paper-ink py-6 text-left first:border-t-2 last:border-b-2 disabled:cursor-not-allowed disabled:opacity-40"
    >
      <IndexNumeral value={index + 1} variant="hollow" className="w-[52px] shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[26px] font-extrabold tracking-[-0.03em] text-paper-ink">
          {provider.label}
        </span>
        <span className="mt-0.5 block truncate text-[13px] text-paper-secondary">
          {providerHint(provider.id, provider.label)}
        </span>
      </span>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-paper-ink">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-accent"
          aria-hidden="true"
        >
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      </span>
    </button>
  );
}

export default function Login() {
  const { user, loading: sessionLoading } = useAuth();
  const location = useLocation();
  const [params] = useSearchParams();

  const [providers, setProviders] = useState<AuthProviderOption[] | null>(null);
  const [siteKey, setSiteKey] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [token, setToken] = useState('');
  const [widgetNonce, setWidgetNonce] = useState(0);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [starting, setStarting] = useState<AuthProviderOption['id'] | null>(null);
  const [retrying, setRetrying] = useState(false);
  const failureCountRef = useRef(0);
  const seqRef = useRef(0);

  const load = useCallback((signal?: AbortSignal, source: 'mount' | 'retry' | 'auto' = 'mount') => {
    const seq = ++seqRef.current;
    if (source === 'retry') {
      failureCountRef.current = 0;
      setError(null);
    }
    if (source === 'auto') setRetrying(true);
    return api
      .get<ProvidersResponse>('/auth/providers', signal ? { signal } : {})
      .then((res) => {
        if (seq !== seqRef.current) return;
        failureCountRef.current = 0;
        setProviders(res.providers);
        setSiteKey(res.turnstile?.siteKey ?? null);
        setError(null);
      })
      .catch((err: unknown) => {
        if (seq !== seqRef.current) return;
        // A superseded request under StrictMode's double mount is not a failure.
        if (err instanceof DOMException && err.name === 'AbortError') return;
        failureCountRef.current += 1;
        setProviders(null);
        setSiteKey(null);
        setError(err instanceof ApiError ? err : new ApiError(0, 'error', 'Something went wrong.'));
      })
      .finally(() => {
        if (seq !== seqRef.current) return;
        setRetrying(false);
      });
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal, 'mount');
    return () => ctrl.abort();
  }, [load]);

  // Automatic retry with exponential backoff while the API is down. The error
  // and Retry stay on screen; this is not a tight loop.
  useEffect(() => {
    if (error === null) return;
    const ctrl = new AbortController();
    const delay = providersRetryDelayMs(failureCountRef.current);
    const timer = window.setTimeout(() => {
      void load(ctrl.signal, 'auto');
    }, delay);
    return () => {
      window.clearTimeout(timer);
      ctrl.abort();
    };
  }, [error, load]);

  // Where sign-in should land. RequireAuth and the global 401 handler both put
  // the attempted path in history state; `?next=` is the fallback for a link
  // somebody constructed by hand.
  const redirect = safeRedirect((location.state as LoginRedirectState | null)?.from ?? params.get('next'));

  const callbackError = params.get('error');
  const callbackMessage = callbackError
    ? (ERROR_COPY[callbackError] ?? 'We could not sign you in. Please try again.')
    : null;

  const start = async (provider: AuthProviderOption) => {
    if (siteKey && token === '') {
      setCaptchaError('Complete the security check before choosing an account.');
      return;
    }
    setStarting(provider.id);
    setCaptchaError(null);
    try {
      const res = await api.post<{ location: string }>('/auth/login', {
        provider: provider.id,
        redirect,
        ...(token !== '' ? { turnstileToken: token } : {}),
      });
      if (typeof res.location !== 'string' || !isHttpUrl(res.location)) {
        throw new ApiError(0, 'error', 'Sign-in returned an unusable destination.');
      }
      window.location.assign(res.location);
    } catch (err: unknown) {
      setStarting(null);
      setToken('');
      setWidgetNonce((n) => n + 1);
      if (err instanceof ApiError && err.code === 'captcha') {
        setCaptchaError(err.message);
        return;
      }
      setCaptchaError(err instanceof ApiError ? err.message : 'Could not start sign-in. Please try again.');
    }
  };

  // Deciding between "signed in" and "signed out" takes one request. Rendering
  // the card first and yanking it away a moment later reads as a glitch on the
  // first screen of the product, so the probe is waited out.
  if (sessionLoading && !user) return <FullPageLoader label="Checking your session" />;
  if (user) return <Navigate to={redirect} replace />;

  const captchaRequired = siteKey !== null;
  const captchaBlocking = captchaRequired && token === '';

  return (
    <div className="flex min-h-screen flex-wrap items-stretch">
      <div className="relative flex min-w-0 flex-[1_1_560px] flex-col overflow-hidden px-8 py-9 sm:px-[52px]">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -right-[70px] -top-[90px] select-none font-sans text-[460px] font-black leading-none tracking-[-0.06em] text-ground-panel"
        >
          01
        </span>
        <Logo className="relative z-[1]" />
        <div className="relative z-[1] flex flex-1 flex-col justify-center py-12">
          <div className="mb-[22px] flex items-center gap-3">
            <span className="h-1 w-9 bg-accent" aria-hidden="true" />
            <span className="label-caps">Customer control panel</span>
          </div>
          <h1 className="mb-[26px] text-[clamp(60px,8vw,104px)] font-black leading-[0.88] tracking-[-0.05em] text-ink-primary">
            Sign in
            <br />
            <span className="text-ink-tertiary">to run it.</span>
          </h1>
          <p className="max-w-[440px] text-[17px] leading-relaxed text-pretty text-ink-secondary">
            {providers && providers.length > 1
              ? 'Either account works. VGNCloud and ValueGameNode sign you in to the same servers, and you will land back here when you are done.'
              : 'You will be sent to your account provider to confirm it is you, then brought straight back here.'}
          </p>
        </div>
        <p className="relative z-[1] text-[12px] text-ink-tertiary">
          Trouble getting in? Reach us from the{' '}
          <Link
            to="/"
            className="underline decoration-ink-tertiary/40 underline-offset-2 hover:text-ink-secondary"
          >
            main VGNCloud site
          </Link>
          .
        </p>
      </div>

      <div className="paper-field flex min-w-0 flex-[1_1_480px] flex-col justify-center px-8 py-[52px] sm:px-12">
        {callbackMessage ? (
          <div className="mb-9 border-l-4 border-accent py-0.5 pl-4">
            <p className="mb-1 text-[15px] font-bold text-paper-ink">We could not sign you in</p>
            <p className="text-[13px] leading-normal text-paper-secondary">{callbackMessage}</p>
          </div>
        ) : null}

        <div className="mb-[22px] flex items-baseline justify-between gap-4">
          <h2 className="text-[28px] font-black tracking-[-0.03em] text-paper-ink">Choose an account</h2>
          <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-paper-secondary">
            Same servers, either door
          </span>
        </div>

        {providers === null && error === null ? (
          <div className="space-y-3" aria-hidden="true">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : null}

        {error ? (
          <div className="space-y-5">
            <div className="border-l-4 border-accent py-0.5 pl-4">
              <p className="mb-1 text-[15px] font-bold text-paper-ink">Sign-in options could not be loaded</p>
              <p className="text-[13px] leading-normal text-paper-secondary">{error.message}</p>
            </div>
            <PaperButton variant="secondary" loading={retrying} onClick={() => void load(undefined, 'retry')}>
              Retry
            </PaperButton>
          </div>
        ) : null}

        {providers !== null && providers.length === 0 ? (
          <div>
            <h3 className="text-[22px] font-black tracking-[-0.03em] text-paper-ink">Sign-in is unavailable</h3>
            <p className="mt-2 max-w-[380px] text-[14px] leading-relaxed text-paper-secondary">
              No sign-in provider is configured on this panel yet. If you were expecting to get in, contact support and
              quote this page.
            </p>
          </div>
        ) : null}

        {providers !== null && providers.length > 0 ? (
          <div>
            {siteKey ? (
              <div className="mb-6">
                <TurnstileWidget
                  key={widgetNonce}
                  siteKey={siteKey}
                  action="login"
                  theme="light"
                  onToken={setToken}
                  onExpire={() => setToken('')}
                  onError={() => {
                    setToken('');
                    setCaptchaError('The security check could not be loaded. Please refresh and try again.');
                  }}
                />
                {captchaError ? (
                  <p className="mt-3 text-[13px] leading-normal text-paper-secondary">{captchaError}</p>
                ) : captchaBlocking ? (
                  <p className="mt-3 text-[13px] leading-normal text-paper-secondary">
                    Complete the security check to choose an account.
                  </p>
                ) : null}
              </div>
            ) : captchaError ? (
              <p className="mb-4 text-[13px] leading-normal text-paper-secondary">{captchaError}</p>
            ) : null}

            {providers.map((provider, index) => (
              <ProviderButton
                key={provider.id}
                provider={provider}
                index={index}
                disabled={false}
                pending={starting !== null}
                onPick={(p) => void start(p)}
              />
            ))}
            <p className="mt-6 max-w-[380px] text-[12px] leading-normal text-paper-tertiary">
              No password is typed here. Accounts are created on the storefront when a server is bought.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
